// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { View } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { StandaloneFilterSelect } from "@/components/ui/filter-select";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Switch } from "@/components/ui/switch";
import { useChannelStatuses } from "@/hooks/api/use-channel-metrics";
import { useChannelStatusSelection } from "@/hooks/use-channel-status-selection";

interface ChannelStatusSelectorProps {
  channelType?: string;
}

export function ChannelStatusSelector({
  channelType,
}: ChannelStatusSelectorProps) {
  const { data: statusesData, isLoading } = useChannelStatuses(channelType);
  const {
    selectedStatuses,
    handleStatusesChange,
    hasOverride,
    auto,
    setAuto,
    clearAllOverrides,
  } = useChannelStatusSelection(channelType);

  // Function to get default engagement statuses
  const getDefaultEngagementStatuses = useCallback((): string[] => {
    if (
      channelType &&
      statusesData?.statuses &&
      statusesData.statuses.length > 0
    ) {
      // Get statuses that represent engagement
      const engagementStatuses = statusesData.statuses
        .filter((statusObj) => statusObj.represents_engagement)
        .map((statusObj) => statusObj.status);

      return engagementStatuses;
    }
    return [];
  }, [channelType, statusesData]);

  // Compute server defaults and only show them; do not write to storage until user changes
  const defaultEngagementStatuses = useMemo(
    () => getDefaultEngagementStatuses(),
    [getDefaultEngagementStatuses],
  );

  // Automatic mode is page-level for channels as well
  const [automatic, setAutomatic] = useState(!!auto);
  useEffect(() => {
    setAutomatic(!!auto);
  }, [auto]);

  // Seed defaults into overrides when stored Auto is OFF and no override exists for this channel
  useEffect(() => {
    if (
      !auto &&
      channelType &&
      !hasOverride &&
      defaultEngagementStatuses.length > 0
    ) {
      handleStatusesChange([...defaultEngagementStatuses]);
    }
  }, [
    auto,
    channelType,
    hasOverride,
    defaultEngagementStatuses,
    handleStatusesChange,
  ]);

  // Format options for filter
  const statusOptions =
    statusesData?.statuses?.map((statusObj) => ({
      id: statusObj.status,
      label: statusObj.status,
    })) || [];

  // Show loading state if necessary
  if (isLoading || !channelType) {
    return (
      <div className="h-8 w-[200px] bg-muted animate-pulse rounded-lg corner-smooth-lg"></div>
    );
  }

  return (
    <StandaloneFilterSelect
      options={statusOptions}
      selectedValues={
        automatic
          ? [...defaultEngagementStatuses]
          : hasOverride
            ? selectedStatuses
            : defaultEngagementStatuses
      }
      triggerPrefix="Statuses"
      triggerPrefixIcon={<View className="h-4 w-4" />}
      emptyTriggerText="none selected"
      valueTextOverride={automatic ? "Auto" : undefined}
      onSelectionChange={(vals) =>
        handleStatusesChange(vals, { intentionalClear: vals.length === 0 })
      }
      label="Qualifying statuses"
      emptyMessage="No statuses available"
      searchPlaceholder="Search statuses..."
      width={"auto"}
      applyButtonText="Apply"
      triggerClassName="justify-between h-8 px-3 text-xs"
      popoverHeader={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Automatic</span>
            <InfoTooltip
              trigger={
                <button className="cursor-pointer">
                  <InfoCircledIcon className="h-4 w-4 text-muted-foreground" />
                </button>
              }
              side="bottom"
              className="w-[280px]"
            >
              <div className="text-sm space-y-2">
                <p>
                  Uses recommended engagement statuses (those marked
                  <span className="font-medium"> Response</span> in Salesforce).
                </p>
                <p>
                  Turn off to customize which statuses qualify as engagement.
                </p>
              </div>
            </InfoTooltip>
          </div>
          <Switch checked={automatic} onCheckedChange={setAutomatic} />
        </div>
      }
      contentDisabled={automatic}
      onApply={(values?: string[]) => {
        if (automatic) {
          clearAllOverrides();
          setAuto(true);
          return true;
        }
        setAuto(false);
        if (!values || values.length === 0) {
          handleStatusesChange([], { intentionalClear: true });
          return true;
        }
        return false;
      }}
      onCancel={() => {
        setAutomatic(!!auto);
      }}
      popover={{
        title: "Qualifying Engagement Statuses",
        content: (
          <p>
            Select statuses that represent engagement with your channel. This
            will filter the metrics displayed in the channel report card.
          </p>
        ),
        width: "280px",
      }}
    />
  );
}
